import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import Button from '@/components/ui/Button';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

export default function Navbar() {
  const { t } = useTranslation();
  const { current } = useLanguage();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const heroPaths = [`/${current}`, `/${current}/destinations`, `/${current}/packages`, `/${current}/about`];
  const hasHero = heroPaths.includes(location.pathname);
  const overHero = hasHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const links: Array<{ to: string; label: string }> = [
    { to: `/${current}`, label: t('nav.home') },
    { to: `/${current}/destinations`, label: t('nav.destinations') },
    { to: `/${current}/packages`, label: t('nav.packages') },
    { to: `/${current}/about`, label: t('nav.about') },
    { to: `/${current}/contact`, label: t('nav.contact') },
  ];

  return (
    <motion.header
      className={cn(
        'fixed top-0 inset-x-0 z-30 transition-colors duration-300',
        overHero
          ? 'bg-gradient-to-b from-ink-900/55 via-ink-900/25 to-transparent'
          : 'bg-sand-50/95 backdrop-blur-md border-b border-neutral-100 shadow-sm',
      )}
      animate={{ y: 0 }}
      initial={{ y: -80 }}
      transition={{ duration: 0.7, ease: EASE_OUT }}
    >
      <div className="container-page flex items-center justify-between h-16 md:h-20">
        <Link
          to={`/${current}`}
          className={cn(
            'font-display text-xl md:text-2xl font-medium transition-colors',
            overHero ? 'text-sand-50 [text-shadow:0_1px_8px_rgba(0,0,0,0.35)]' : 'text-ink-900',
          )}
        >
          Destination<span className="italic"> Paradis</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === `/${current}`}
              className={({ isActive }) =>
                cn(
                  'relative text-sm font-medium transition-colors py-1',
                  overHero
                    ? cn(
                        'text-sand-50/95 hover:text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]',
                        isActive &&
                          'text-white after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-gold-400',
                      )
                    : cn(
                        'text-ink-700 hover:text-teal-600',
                        isActive &&
                          'text-teal-600 after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-teal-500',
                      ),
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher className="hidden sm:flex" overHero={overHero} />
          <Link
            to={`/${current}/${isAuthenticated ? 'profile' : 'login'}`}
            className={cn(
              'hidden md:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md transition-colors',
              overHero
                ? 'text-sand-50/95 hover:text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]'
                : 'text-ink-700 hover:text-teal-600',
            )}
            aria-label={isAuthenticated ? t('nav.profile') : t('nav.login')}
          >
            <User size={16} />
            <span>{isAuthenticated ? t('nav.profile') : t('nav.login')}</span>
          </Link>
          <Link to={`/${current}/packages`} className="hidden md:inline-block">
            <Button variant="primary" size="sm">
              {t('nav.bookCta')}
            </Button>
          </Link>
          <button
            className={cn(
              'lg:hidden p-2 -mr-2 transition-colors',
              overHero ? 'text-sand-50' : 'text-ink-900',
            )}
            aria-label="Menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((s) => !s)}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mobileOpen ? (
                <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <X size={22} />
                </motion.span>
              ) : (
                <motion.span key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <Menu size={22} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile drawer — AnimatePresence */}
      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            key="drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
            className="lg:hidden overflow-hidden bg-sand-50 border-t border-neutral-100 shadow-md"
          >
            <nav className="container-page flex flex-col py-4 gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3, ease: EASE_OUT }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === `/${current}`}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block py-3 px-2 text-base font-medium border-b border-neutral-100',
                        isActive ? 'text-teal-600' : 'text-ink-700',
                      )
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: links.length * 0.05, duration: 0.3, ease: EASE_OUT }}
              >
                <Link
                  to={`/${current}/${isAuthenticated ? 'profile' : 'login'}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex py-3 px-2 text-base font-medium text-ink-700 items-center gap-2 border-b border-neutral-100"
                >
                  <User size={16} />
                  {isAuthenticated ? t('nav.profile') : t('nav.login')}
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (links.length + 1) * 0.05, duration: 0.3, ease: EASE_OUT }}
                className="mt-3 pt-3 border-t border-neutral-100"
              >
                <p className="text-xs font-semibold text-ink-400 uppercase tracking-wide mb-2 px-2">
                  Idioma
                </p>
                <LanguageSwitcher inline />
                <Link
                  to={`/${current}/packages`}
                  onClick={() => setMobileOpen(false)}
                  className="block mt-4"
                >
                  <Button variant="primary" size="sm" className="w-full justify-center">
                    {t('nav.bookCta')}
                  </Button>
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
